/**
 * expressionParser — reverse of SdmCodeGen's formula generators.
 *
 * Turns a generated OpenEDC decision-table FormalExpression string back into the structured
 * { branches, defaultValue } the LinkModal builder edits. This is what makes an EXISTING formula
 * mapping editable: after a publish + reload the authored LinkDef is gone (LinkDefs are never
 * written to the wire SDM), so the only durable representation is the MethodDef expression text.
 *
 * SAFE BY CONSTRUCTION: every parse is verified by re-running the forward generator
 * (buildFormulaTableExpression) and byte-comparing to the original. If the round-trip is not
 * lossless (hand-written / non-canonical expressions, nested grouping the flat builder can't
 * model, deletion flags, etc.) the parser returns null and the caller leaves the formula
 * read-only. It never silently produces a wrong structure.
 */
import type { ConditionClause, FormulaBranch, LinkDef, OID, TriggerOperator, TriggerValue } from '../types';
import { buildFormulaTableExpression, buildDeletionExpressionFromBranches } from './SdmCodeGen';

export interface ParsedFormula {
  branches: FormulaBranch[];
  defaultValue: string;
}

const SRC = 'I\\.\\d+';

/** Indices of commas at paren-depth 0, ignoring those inside double-quoted strings. */
function topLevelCommas(s: string): number[] {
  const out: number[] = [];
  let depth = 0;
  let inStr = false;
  for (let i = 0; i < s.length; i++) {
    const c = s[i];
    if (inStr) { if (c === '"') inStr = false; continue; }
    if (c === '"') { inStr = true; continue; }
    if (c === '(') depth++;
    else if (c === ')') depth--;
    else if (c === ',' && depth === 0) out.push(i);
  }
  return out;
}

/** Parse a canonical 3-arg `IF(cond, value, rest)`; null if not exactly that shape. */
function parseIf(text: string): [string, string, string] | null {
  const t = text.trim();
  if (!t.startsWith('IF(') || !t.endsWith(')')) return null;
  const inner = t.slice(3, -1);
  const commas = topLevelCommas(inner);
  if (commas.length !== 2) return null;
  return [
    inner.slice(0, commas[0]).trim(),
    inner.slice(commas[0] + 1, commas[1]).trim(),
    inner.slice(commas[1] + 1).trim(),
  ];
}

/** Split a condition on top-level ` AND ` / ` OR `, returning each clause's preceding connector. */
function splitConnectors(s: string): Array<{ expr: string; connector?: 'AND' | 'OR' }> {
  const segments: string[] = [];
  const ops: Array<'AND' | 'OR'> = [];
  let depth = 0;
  let inStr = false;
  let last = 0;
  for (let i = 0; i < s.length; i++) {
    const c = s[i];
    if (inStr) { if (c === '"') inStr = false; continue; }
    if (c === '"') { inStr = true; continue; }
    if (c === '(') depth++;
    else if (c === ')') depth--;
    else if (depth === 0) {
      if (s.startsWith(' AND ', i)) { segments.push(s.slice(last, i)); ops.push('AND'); i += 4; last = i + 1; }
      else if (s.startsWith(' OR ', i)) { segments.push(s.slice(last, i)); ops.push('OR'); i += 3; last = i + 1; }
    }
  }
  segments.push(s.slice(last));
  return segments.map((expr, idx) => ({ expr: expr.trim(), connector: idx === 0 ? undefined : ops[idx - 1] }));
}

/** Strip exactly one wrapping paren layer if it encloses the whole string. */
function unwrap(s: string): string {
  const t = s.trim();
  if (!t.startsWith('(') || !t.endsWith(')')) return t;
  let depth = 0;
  let inStr = false;
  for (let i = 0; i < t.length; i++) {
    const c = t[i];
    if (inStr) { if (c === '"') inStr = false; continue; }
    if (c === '"') { inStr = true; continue; }
    if (c === '(') depth++;
    else if (c === ')') { depth--; if (depth === 0 && i !== t.length - 1) return t; }
  }
  return t.slice(1, -1).trim();
}

/** Reverse one clause (buildClauseExpression output) back to {field, op, values}; null if unknown. */
function parseClause(raw: string): { field: OID; op: TriggerOperator; values: TriggerValue[] } | null {
  const s = raw.trim();
  let m: RegExpMatchArray | null;

  // is_not_empty: !((SRC == "") OR (SRC == null))
  m = s.match(new RegExp(`^!\\(\\((${SRC}) == ""\\) OR \\(\\1 == null\\)\\)$`));
  if (m) return { field: m[1], op: 'is_not_empty', values: [] };

  // is_empty: (SRC == "") OR (SRC == null)
  m = s.match(new RegExp(`^\\((${SRC}) == ""\\) OR \\(\\1 == null\\)$`));
  if (m) return { field: m[1], op: 'is_empty', values: [] };

  // not_contains: one or more !(("," + SRC + ",") contains ",V,") joined by ' AND '
  if (/^!\(/.test(s)) {
    const parts = s.split(' AND ');
    const re = new RegExp(`^!\\(\\("," \\+ (${SRC}) \\+ ","\\) contains ",(.*)",\\)$|^!\\(\\("," \\+ (${SRC}) \\+ ","\\) contains ",(.*),"\\)$`);
    const vals: TriggerValue[] = [];
    let field = '';
    let ok = true;
    for (const p of parts) {
      const pm = p.trim().match(new RegExp(`^!\\(\\("," \\+ (${SRC}) \\+ ","\\) contains ",(.*),"\\)$`));
      if (!pm) { ok = false; break; }
      field = pm[1];
      vals.push(pm[2]);
    }
    if (ok && field) return { field, op: 'not_contains', values: vals };
    void re;
  }

  // contains (single): ("," + SRC + ",") contains ",V,"
  m = s.match(new RegExp(`^\\("," \\+ (${SRC}) \\+ ","\\) contains ",(.*),"$`));
  if (m) return { field: m[1], op: 'contains', values: [m[2]] };

  // contains (multi): (("," + SRC + ",") contains ",v1," OR ("," + SRC + ",") contains ",v2,")
  if (s.includes(' contains ') && s.includes(' OR ')) {
    const inner = unwrap(s);
    const parts = inner.split(' OR ');
    const vals: TriggerValue[] = [];
    let field = '';
    let ok = true;
    for (const p of parts) {
      const pm = p.trim().match(new RegExp(`^\\("," \\+ (${SRC}) \\+ ","\\) contains ",(.*),"$`));
      if (!pm) { ok = false; break; }
      field = pm[1];
      vals.push(pm[2]);
    }
    if (ok && field) return { field, op: 'contains', values: vals };
  }

  // eq (multi): (SRC == "v1" OR SRC == "v2")
  if (s.startsWith('(') && s.includes(' OR ') && s.includes(' == ')) {
    const inner = unwrap(s);
    const parts = inner.split(' OR ');
    const vals: TriggerValue[] = [];
    let field = '';
    let ok = true;
    for (const p of parts) {
      const pm = p.trim().match(new RegExp(`^(${SRC}) == "(.*)"$`));
      if (!pm) { ok = false; break; }
      field = pm[1];
      vals.push(pm[2]);
    }
    if (ok && field) return { field, op: 'eq', values: vals };
  }

  // ne: SRC != "V"
  m = s.match(new RegExp(`^(${SRC}) != "(.*)"$`));
  if (m) return { field: m[1], op: 'ne', values: [m[2]] };

  // eq (single): SRC == "V"
  m = s.match(new RegExp(`^(${SRC}) == "(.*)"$`));
  if (m) return { field: m[1], op: 'eq', values: [m[2]] };

  // numeric thresholds: parseFloat(SRC) >= N  (>=, <=, >, < — order matters)
  m = s.match(new RegExp(`^parseFloat\\((${SRC})\\) (>=|<=|>|<) (.+)$`));
  if (m) {
    const op = ({ '>': 'gt', '>=': 'gte', '<': 'lt', '<=': 'lte' } as const)[m[2] as '>' | '>=' | '<' | '<='];
    return { field: m[1], op, values: [m[3].trim()] };
  }

  return null;
}

/** Reverse a branch condition string into ConditionClause[]; null if any clause is unknown. */
function parseConditions(cond: string): ConditionClause[] | null {
  const c = cond.trim();
  if (c === 'true' || c === '') return [];
  const parts = splitConnectors(c);
  const clauses: ConditionClause[] = [];
  for (const part of parts) {
    const parsed = parseClause(unwrap(part.expr));
    if (!parsed) return null;
    clauses.push({ ...parsed, ...(part.connector ? { connector: part.connector } : {}) });
  }
  return clauses;
}

/** Reverse a value expression into {formulaTemplate, formulaVars} by extracting parseFloat tokens. */
function parseValue(value: string): { formulaTemplate: string; formulaVars: Record<string, OID | null> } {
  const vars: Record<string, OID | null> = {};
  const names = 'abcdefghijklmnopqrstuvwxyz';
  let i = 0;
  const template = value.replace(new RegExp(`\\(parseFloat\\((${SRC})\\)\\|\\|0\\)`, 'g'), (_m, oid: string) => {
    const name = i < names.length ? names[i] : `v${i}`;
    i += 1;
    vars[name] = oid;
    return name;
  });
  return { formulaTemplate: template, formulaVars: vars };
}

/**
 * Parse a generated formula decision-table expression into the builder's structured form.
 * Returns null when the text is not a canonical builder formula (e.g. a deletion flag, a bare
 * passthrough, or a hand-written expression) or when the parse does not round-trip losslessly.
 */
export function parseFormulaTableExpression(text: string | undefined | null): ParsedFormula | null {
  if (!text) return null;
  const original = text.trim();
  if (!original.startsWith('IF(')) return null; // not a decision table (passthrough/bare value)

  const branchesRaw: Array<{ cond: string; value: string }> = [];
  let cur = original;
  let guard = 0;
  while (cur.startsWith('IF(') && guard++ < 100) {
    const parsed = parseIf(cur);
    if (!parsed) return null;
    const [cond, value, rest] = parsed;
    branchesRaw.push({ cond, value });
    cur = rest.trim();
  }
  const defaultValue = cur;

  // A table whose every branch yields `true` is a pure DELETION — parseDeletionToBranches owns it
  // and reconstructs a Remove card, so it must not be claimed here. A table that MIXES outcomes is
  // a different animal: "populate this count, else remove the task, else leave it alone" is one
  // rule the Remove builder cannot express, and rejecting it sent the whole thing to the raw text
  // editor. Those come through, and the round-trip guard below is what keeps the parse honest.
  if (branchesRaw.length > 0 && branchesRaw.every((b) => b.value === 'true')) return null;

  const branches: FormulaBranch[] = [];
  for (const br of branchesRaw) {
    const conditions = parseConditions(br.cond);
    if (conditions === null) return null;
    const { formulaTemplate, formulaVars } = parseValue(br.value);
    branches.push({ conditions, formulaTemplate, formulaVars });
  }

  const result: ParsedFormula = { branches, defaultValue };

  // Round-trip guard: regenerate and compare. Only accept a lossless parse.
  const rebuilt = buildFormulaTableExpression(
    { populate: { branches: result.branches, defaultValue: result.defaultValue } } as unknown as LinkDef,
  ).trim();
  if (rebuilt !== original) return null;

  return result;
}

/**
 * Parse a DELETION expression (a decision table whose branch values are all the literal `true`)
 * into the Remove builder's branches. Handles both shapes that exist in the wild:
 *   A. mis-saved / formula-generated: `IF(cond, true, 0)` (possibly multi-branch IF/ELSE-IF)
 *   B. canonical deletion: `IF((c1) OR (c2) ..., true, null)` (OR-joined branch groups)
 * Each returned branch carries formulaTemplate:'true' (the Remove path ignores the value).
 * Dual round-trip guard against both generators; returns null if neither reproduces the original.
 */
export function parseDeletionToBranches(text: string | undefined | null): FormulaBranch[] | null {
  if (!text) return null;
  const original = text.trim();
  if (!original.startsWith('IF(')) return null;

  const raw: Array<{ cond: string; value: string }> = [];
  let cur = original;
  let guard = 0;
  while (cur.startsWith('IF(') && guard++ < 100) {
    const parsed = parseIf(cur);
    if (!parsed) return null;
    raw.push({ cond: parsed[0], value: parsed[1] });
    cur = parsed[2].trim();
  }
  const defaultValue = cur;
  if (raw.length === 0) return null;
  // A deletion table outputs `true` on every branch (delete when matched).
  if (!raw.every((b) => b.value === 'true')) return null;

  const toBranch = (cond: string): FormulaBranch | null => {
    const conditions = parseConditions(cond);
    if (conditions === null) return null;
    return { conditions, formulaTemplate: 'true', formulaVars: {} };
  };

  // Interpretation A: each IF level is its own rule (matches the formula-table generator).
  const aBranches = raw.map((b) => toBranch(b.cond));
  if (aBranches.every((b): b is FormulaBranch => b !== null)) {
    const rebuilt = buildFormulaTableExpression(
      { populate: { branches: aBranches, defaultValue } } as unknown as LinkDef,
    ).trim();
    if (rebuilt === original) return aBranches;
  }

  // Interpretation B: a single IF whose condition is OR-joined branch groups (canonical deletion).
  if (raw.length === 1) {
    const parts = splitConnectors(raw[0].cond);
    if (parts.slice(1).every((p) => p.connector === 'OR')) {
      const bBranches = parts.map((p) => toBranch(unwrap(p.expr)));
      if (bBranches.every((b): b is FormulaBranch => b !== null)) {
        const rebuilt = buildDeletionExpressionFromBranches(bBranches).trim();
        if (rebuilt === original) return bBranches;
      }
    }
  }

  return null;
}

export interface ParsedSwitchPlan {
  field: OID;
  operator: TriggerOperator;
  values: TriggerValue[];
  pickedPlanUid: string;
  fallbackPlanUid: string;
}

/** Parse a JSON string literal (e.g. `"abc-123"`) back to its bare value; null for anything else
 *  (unquoted / numeric / malformed) — a plan UID is never emitted unquoted (see
 *  buildSwitchPlanExpression), so anything that doesn't unquote to a string isn't a Switch-Plan
 *  branch value. */
function unquoteJsonString(s: string): string | null {
  try {
    const v = JSON.parse(s);
    return typeof v === 'string' ? v : null;
  } catch {
    return null;
  }
}

/**
 * Reverse-engineers a Switch-Plan link's derived MethodDef expression back into
 * {field, operator, values, pickedPlanUid, fallbackPlanUid}, so a reloaded (post publish+reload)
 * link reconstructs as an editable card in the Active Links column — exactly like
 * parseDeletionTrigger / parseFormulaTableExpression already do for their own link types.
 *
 * A Switch-Plan expression is a 2-branch quoted-string formula table (an is_empty guard branch +
 * one match-clause branch, both quoted-string values, self-consistent with the final ELSE
 * default) — thin wrapper over the EXISTING parseFormulaTableExpression, which already round-trip
 * verifies the shape; this only adds the Switch-Plan-specific structural + self-consistency
 * checks on top.
 */
export function parseSwitchPlanExpression(text: string | undefined | null): ParsedSwitchPlan | null {
  const parsed = parseFormulaTableExpression(text);
  if (!parsed || parsed.branches.length !== 2) return null;
  const [b0, b1] = parsed.branches;
  if (b0.conditions.length !== 1 || b0.conditions[0].op !== 'is_empty') return null;
  if (b1.conditions.length !== 1) return null;
  const field = b0.conditions[0].field;
  if (b1.conditions[0].field !== field) return null;
  const fallback = unquoteJsonString(b0.formulaTemplate);
  const picked = unquoteJsonString(b1.formulaTemplate);
  const fallbackDefault = unquoteJsonString(parsed.defaultValue);
  if (fallback === null || picked === null || fallbackDefault === null) return null;
  if (fallback !== fallbackDefault) return null;
  return {
    field, operator: b1.conditions[0].op, values: b1.conditions[0].values,
    pickedPlanUid: picked, fallbackPlanUid: fallback,
  };
}

export interface ParsedDeletionTrigger {
  field: OID;
  op: TriggerOperator;
  values: string[];
}

/**
 * Recognize a SINGLE-TRIGGER deletion expression produced by SdmCodeGen.buildDeletionExpression
 * (the operator table in SDM_CODEGEN_PATTERNS.md) and return its {field, operator, values}.
 *
 * Unlike parseDeletionToBranches this matches the EXACT generator shapes directly (the match is
 * the validation — no round-trip guard), so it recognizes the builder's own output even though
 * buildDeletionExpression and buildDeletionExpressionFromBranches parenthesize differently.
 * Whitespace-tolerant so hand-seeded production expressions (e.g. `"," + SRC + ","` vs
 * `","+ SRC + ","`) match too. Returns null if it isn't a recognized single-trigger deletion.
 */
export function parseDeletionTrigger(text: string | undefined | null): ParsedDeletionTrigger | null {
  if (!text) return null;
  const t = text.replace(/\s+/g, ' ').trim();
  if (!t.startsWith('IF(')) return null;
  const S = 'I\\.\\d+';
  let m: RegExpMatchArray | null;

  // is_not_empty: IF((S == "") OR (S == null), null, true)
  m = t.match(new RegExp(`^IF\\(\\((${S}) == ""\\) OR \\(\\1 == null\\), null, true\\)$`));
  if (m) return { field: m[1], op: 'is_not_empty', values: [] };

  // is_empty: IF((S == "") OR (S == null), true, null)
  m = t.match(new RegExp(`^IF\\(\\((${S}) == ""\\) OR \\(\\1 == null\\), true, null\\)$`));
  if (m) return { field: m[1], op: 'is_empty', values: [] };

  // ne: IF((S == "") OR (S == null), true, IF(S != "V", true, null))
  m = t.match(new RegExp(`^IF\\(\\((${S}) == ""\\) OR \\(\\1 == null\\), true, IF\\(\\1 != "(.*?)", true, null\\)\\)$`));
  if (m) return { field: m[1], op: 'ne', values: [m[2]] };

  // gt / gte / lt / lte: IF((S == "") OR (S == null), null, IF(parseFloat(S) OP N, true, null))
  m = t.match(new RegExp(`^IF\\(\\((${S}) == ""\\) OR \\(\\1 == null\\), null, IF\\(parseFloat\\(\\1\\) (>=|<=|>|<) (.+?), true, null\\)\\)$`));
  if (m) {
    const op = ({ '>': 'gt', '>=': 'gte', '<': 'lt', '<=': 'lte' } as const)[m[2] as '>' | '>=' | '<' | '<='];
    return { field: m[1], op, values: [m[3].trim()] };
  }

  // Empty-guarded inner block: IF((S == "") OR (S == null), true, IF(<inner>, true, null))
  m = t.match(new RegExp(`^IF\\(\\((${S}) == ""\\) OR \\(\\1 == null\\), true, IF\\((.+), true, null\\)\\)$`));
  if (m) {
    const field = m[1];
    const inner = m[2];
    // not_contains: one or more  !(("," + S + ",") contains ",V,")  joined by AND
    if (inner.includes('contains') && inner.includes('!')) {
      const vals = Array.from(inner.matchAll(/contains ",(.*?),"/g)).map(x => x[1]);
      if (vals.length) return { field, op: 'not_contains', values: vals };
    }
    // not_in: (S != "V1" AND S != "V2")
    if (inner.includes(' != ') && inner.includes(' AND ')) {
      const vals = Array.from(inner.matchAll(new RegExp(`${S} != "(.*?)"`, 'g'))).map(x => x[1]);
      if (vals.length) return { field, op: 'not_in', values: vals };
    }
  }

  // in: IF((S == "V1" OR S == "V2"), true, null)
  m = t.match(/^IF\(\((.+)\), true, null\)$/);
  if (m && m[1].includes(' == ') && m[1].includes(' OR ') && !m[1].includes('contains')) {
    const matches = Array.from(m[1].matchAll(new RegExp(`(${S}) == "(.*?)"`, 'g')));
    const fields = new Set(matches.map(x => x[1]));
    if (matches.length && fields.size === 1) return { field: matches[0][1], op: 'in', values: matches.map(x => x[2]) };
  }

  // contains: IF(<("," + S + ",") contains ",V,"  joined by OR>, true, null)
  m = t.match(/^IF\((.+), true, null\)$/);
  if (m && m[1].includes('contains') && !m[1].includes('!')) {
    const fm = m[1].match(new RegExp(`"," ?\\+ ?(${S}) ?\\+ ?","`));
    const vals = Array.from(m[1].matchAll(/contains ",(.*?),"/g)).map(x => x[1]);
    if (fm && vals.length) return { field: fm[1], op: 'contains', values: vals };
  }

  // eq: IF(S == "V", true, null)
  m = t.match(new RegExp(`^IF\\((${S}) == "(.*?)", true, null\\)$`));
  if (m) return { field: m[1], op: 'eq', values: [m[2]] };

  return null;
}
